import { AccessIcon, EmptyState, LazyLoader } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { Page } from '../../../../components/Page/Page';
import { TeachingBlockConflictProvider } from '../../../../components/TeachingBlockGrid/Contexts/TeachingBlockConflictContext';
import { TeachingBlockProvider } from '../../../../components/TeachingBlockGrid/Contexts/TeachingBlockContext';
import { TeachingBlockVersionTopBar } from '../../../../components/TeachingBlockVersion/TeachingBlockVersionTopBar/TeachingBlockVersionTopBar';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import { TeachingBlockDayAvailabilityStore } from '../../../../stores/DayAvailabilityStore';
import { StoreHandler } from '../../../../stores/StoreHandler';
import { use_TeachingBlocksQuery } from '../../../../types/planung-graphql-client-defs';

export const availabilityStoreHandler = new StoreHandler<TeachingBlockDayAvailabilityStore>();

export const TeachingBlockVersion = observer(({ baseSlug }: { baseSlug: string }) => {
  const { t } = useTranslation();
  const { teachingBlockVersionUuid } = useParams();
  const permissions = usePermissionChecker();

  const selectedSchoolYear = useUserConfigContext().selectedSchoolYear;

  const [inSchoolYear, setInSchoolYear] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);

  const [{ data: teachingBlocksData }] = use_TeachingBlocksQuery({
    variables: {
      where: {
        versions_SOME: { uuid: teachingBlockVersionUuid },
      },
    },
    pause: !teachingBlockVersionUuid,
  });

  useEffect(() => {
    if (teachingBlockVersionUuid && selectedSchoolYear) {
      availabilityStoreHandler.register(teachingBlockVersionUuid, () => {
        return new TeachingBlockDayAvailabilityStore(
          dayjs(selectedSchoolYear.start).startOf('day').utc().toDate(),
          dayjs(selectedSchoolYear.end).startOf('day').utc().toDate(),
        );
      });
      setReady(true);
    }
  }, [selectedSchoolYear, teachingBlockVersionUuid]);

  useEffect(() => {
    if (teachingBlockVersionUuid) {
      const inSchoolYear = teachingBlocksData?.teachingBlocks[0].schoolYear.uuid === selectedSchoolYear?.uuid;
      setInSchoolYear(inSchoolYear);
    }
  }, [teachingBlockVersionUuid, teachingBlocksData, selectedSchoolYear]);

  return (
    <Page isForbidden={!permissions?.canReadTimetable()} fullHeight fullWidth>
      {teachingBlockVersionUuid && ready && (
        <>
          {inSchoolYear ? (
            <Suspense fallback={<LazyLoader embedded transparent size='xxl' forceHeight='60vh' />}>
              <TeachingBlockConflictProvider>
                <TeachingBlockProvider>
                  <TeachingBlockVersionTopBar versionUuid={teachingBlockVersionUuid} baseSlug={baseSlug} />
                  <Outlet />
                </TeachingBlockProvider>
              </TeachingBlockConflictProvider>
            </Suspense>
          ) : (
            <EmptyState
              icon={<AccessIcon color='var(--color-error)' />}
              title={t('timetableVersion.notInSchoolYear')}
              forcedHeight='60vh'
            />
          )}
        </>
      )}
    </Page>
  );
});
